import './business-unit-details.scss'

import { ApiBusinessUnit, ApiCompetencyType } from '../../../api/_legacy/generated-api'
import {
  GenericValidationMessages,
  Label,
  SaveSuccessMessage,
  Select,
  Space,
  StrategySelector,
  TextInput,
  ValidationMessage
} from '../../../components/core-v2'
import IconButton from '../../../components/core-v2/molecules/iconButton'
import { isErrorWith } from '../common'
import { getIsChecked, getIsDisabled, setStrategies } from '../common/helpers'
import { BusinessUnit, Client, Mutation, Strategy } from '../types'

type Mutator = (mutation: Mutation<BusinessUnit>) => void

export type Props = {
  businessUnit: BusinessUnit
  client: Client
  errorMessages: string[]
  errors: { [key: string]: string }
  isSaveSuccessful: boolean
  onMutate: Mutator
  onSave: (businessUnit: BusinessUnit) => void
  saveButtonText: string
  strategies: Strategy[]
}

type StrategySelected = 'yes' | 'no' | 'unavailable'

function getState(
  strategyType: string,
  client: Client,
  businessUnit: ApiBusinessUnit
): StrategySelected {
  if (getIsDisabled(strategyType, client.disallowedStrategies)) {
    return 'unavailable'
  }

  return getIsChecked(strategyType, businessUnit.disallowedStrategies) ? 'yes' : 'no'
}

export function BusinessUnitEditor({
  businessUnit,
  client,
  errorMessages,
  errors,
  isSaveSuccessful,
  onMutate,
  onSave,
  saveButtonText,
  strategies = []
}: Props) {
  const strategyPermissions = strategies.map(strat => ({
    id: strat.id,
    name: strat.name,
    selected: getState(strat.id, client, businessUnit)
  }))

  return (
    <Space mx={5} my={6}>
      <Space mb={6}>
        <Label>Business unit name</Label>
        <TextInput
          isError={isErrorWith(errors, 'businessUnitName')}
          maxLength={64}
          value={businessUnit.businessUnitName}
          onChange={e => {
            onMutate(bu => (bu.businessUnitName = e.target.value))
          }}
        />
        <Space fontSize="13px" mt={3}>
          The business unit name entered will be used throughout the site.
        </Space>
        <ValidationMessage errors={errors} fieldName="businessUnitName" />
      </Space>
      <Space mb={6}>
        <Label>Strategies</Label>
        <StrategySelector
          strategies={strategyPermissions}
          onChange={(name, value) => {
            const mutation = (businessUnit: ApiBusinessUnit) => {
              businessUnit.disallowedStrategies = setStrategies(
                name,
                Array.from(
                  new Set([...client.disallowedStrategies, ...businessUnit.disallowedStrategies])
                ),
                value
              )
            }

            onMutate(mutation)
          }}
        />
      </Space>
      <Space mb={6}>
        <Label>Skill level</Label>
        <Select
          onChange={v => {
            onMutate(bu => (bu.competency = v.target.value as ApiCompetencyType))
          }}
        >
          <option value="Entry">Entry</option>
          <option value="Experienced">Experienced</option>
          <option value="Expert">Expert</option>
        </Select>
      </Space>
      <GenericValidationMessages errorMessages={errorMessages} />
      <Space isflex alignItems="center" justifyContent="space-between">
        <IconButton
          disabled={false}
          icon={false}
          iconPosition="left"
          operationType="progress"
          text={saveButtonText}
          onClick={() => onSave(businessUnit)}
        />
        <SaveSuccessMessage isVisible={isSaveSuccessful}>
          Business unit saved successfully!
        </SaveSuccessMessage>
      </Space>
    </Space>
  )
}
